import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ExistingAccountDialog } from './ExistingAccountDialog';
import { useToast } from '@/hooks/use-toast';

export function OAuthCallbackHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showExistingAccountDialog, setShowExistingAccountDialog] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [hasHandledCallback, setHasHandledCallback] = useState(false);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Only handle OAuth callback if we haven't handled it yet and we're on a relevant path
      if (hasHandledCallback) return;
      
      // Check if this looks like an OAuth callback (has access_token or error in URL or fragment)
      const searchParams = new URLSearchParams(location.search);
      const hashParams = new URLSearchParams(location.hash.substring(1)); // Remove # and parse
      const hasAccessToken = searchParams.has('access_token') || hashParams.has('access_token');
      const hasError = searchParams.has('error') || hashParams.has('error');
      const hasCode = searchParams.has('code') || hashParams.has('code');
      
      // Check if we're on the onboarding page and this might be an OAuth flow
      const isOnboardingPage = location.pathname === '/onboarding';
      const isLikelyOAuthFlow = hasAccessToken || hasError || hasCode || 
                               (isOnboardingPage && sessionStorage.getItem('oauth_flow') === 'true');
      
      // Only proceed if this looks like an OAuth callback or we're on onboarding after OAuth
      if (!isLikelyOAuthFlow) return;
      
      setIsChecking(true);
      setHasHandledCallback(true);
      
      try {
        // Wait a moment for Supabase to process the OAuth callback
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Get the current session to see if user is authenticated
        let session;
        let sessionError;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries) {
          try {
            const result = await supabase.auth.getSession();
            session = result.data.session;
            sessionError = result.error;
            break;
          } catch (error: unknown) {
            retryCount++;
            if (error.message?.includes('rate limit') || error.message?.includes('429')) {
              await new Promise(resolve => setTimeout(resolve, retryCount * 2000));
            } else if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
        
        if (sessionError) {
          console.error('Error getting session:', sessionError);
          return;
        }

        if (!session?.user) return;

        // Check if this user already has a subscriber record (indicating they're an existing user)
        let existingSubscriber;
        let subscriberError;
        retryCount = 0;
        
        while (retryCount < maxRetries) {
          try {
            const result = await supabase
              .rpc('get_user_subscription_status', { p_user_id: session.user.id })
              .maybeSingle();
            existingSubscriber = result.data;
            subscriberError = result.error;
            break;
          } catch (error: unknown) {
            retryCount++;
            if (error.message?.includes('rate limit') || error.message?.includes('429')) {
              await new Promise(resolve => setTimeout(resolve, retryCount * 2000));
            } else if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }

        if (subscriberError) {
          console.error('Error checking subscriber:', subscriberError);
          return;
        }

        // Also check if user has completed onboarding
        let userSettings;
        retryCount = 0;
        
        while (retryCount < maxRetries) {
          try {
            const result = await supabase
              .from('user_settings')
              .select('completed_onboarding')
              .eq('user_id', session.user.id)
              .maybeSingle();
            userSettings = result.data;
            break;
          } catch (error: unknown) {
            retryCount++;
            if (error.message?.includes('rate limit') || error.message?.includes('429')) {
              await new Promise(resolve => setTimeout(resolve, retryCount * 2000));
            } else if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }

        // Check multiple indicators of existing account
        const isExistingUser = existingSubscriber || 
                              userSettings?.completed_onboarding === true ||
                              session.user.created_at < new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

        // Determine if this came from a signup flow specifically
        const isSignupFlow = sessionStorage.getItem('oauth_flow') === 'true';
        
        console.log('[OAuthCallbackHandler] Flow analysis:', {
          isExistingUser,
          isSignupFlow,
          currentPath: location.pathname,
          oauthFlowFlag: sessionStorage.getItem('oauth_flow'),
          userId: session.user.id
        });
        
        if (isExistingUser && isSignupFlow) {
          // This is an existing user attempting to sign up - show the dialog
          // Clear the OAuth flow flag since we've handled this user
          sessionStorage.removeItem('oauth_flow');
          console.log('[OAuthCallbackHandler] Showing "Account Already Exists" dialog for signup attempt');
          setShowExistingAccountDialog(true);
          return;
        } else if (isExistingUser && !isSignupFlow) {
          // This is an existing user logging in - let them proceed normally
          // Clear the OAuth flow flag
          sessionStorage.removeItem('oauth_flow');
          console.log('[OAuthCallbackHandler] Existing user logging in via OAuth - proceeding normally');
          return;
        } else if (!isExistingUser) {
          // This is a new user - ALWAYS redirect to onboarding regardless of entry point
          // Clear the OAuth flow flag
          sessionStorage.removeItem('oauth_flow');
          console.log('[OAuthCallbackHandler] New user via OAuth - redirecting to onboarding');
          navigate('/onboarding');
          return;
        }
        
      } catch (error) {
        console.error('Error handling OAuth callback:', error);
        toast({
          title: 'Authentication Error',
          description: 'There was an error processing your sign-in. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setIsChecking(false);
      }
    };
    
    handleOAuthCallback();
    
    // Cleanup function to clear OAuth flow flag when component unmounts
    return () => {
      if (hasHandledCallback) {
        sessionStorage.removeItem('oauth_flow');
      }
    };
  }, [location, navigate, toast, hasHandledCallback]);

  const handleExistingAccountSignIn = () => {
    setShowExistingAccountDialog(false);
    // Sign out the user first, then redirect to login
    supabase.auth.signOut().then(() => {
      navigate('/login', { replace: true });
    });
  };

  const closeDialog = () => {
    setShowExistingAccountDialog(false);
    // Sign out the user and redirect to login
    supabase.auth.signOut().then(() => {
      navigate('/login', { replace: true });
    });
  };

  // Show loading while checking
  if (isChecking) {
    return (
      <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="text-center bg-white p-6 rounded-lg shadow-lg border">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Just a moment...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we verify your information</p>
        </div>
      </div>
    );
  }

  return (
    <ExistingAccountDialog 
      open={showExistingAccountDialog}
      onOpenChange={closeDialog}
      onSignIn={handleExistingAccountSignIn}
    />
  );
}
